import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { combineLatest, concat, forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  map,
  mergeMap,
  repeatWhen,
  retryWhen,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  EquipmentApi,
  ViError,
  RemoteDiagnosticsHelpersService,
  Snapshot,
  SnapshotsConfigurationsBackupResponse,
  SnapshotsResponse,
  SnapshotUploadStatus,
  Vdd,
  VddWithContent,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { BackupSeparator, AuthStore, RemoteDiagnosticsStore, SnapshotType } from '@store';

import { InstallationDetailsStore } from '../installation-details';
import { VddDetailsListStore } from '../vdd-details-list';

import { RemoteDiagnosticsSnapshotsActions } from './remote-diagnostics-snapshots.actions';
import { RemoteDiagnosticsSnapshotsService } from './remote-diagnostics-snapshots.service';
import { RemoteDiagnosticsSnapshotsStore } from './remote-diagnostics-snapshots.store';
import { SnapshotUpdateResponseTransformed } from './remote-diagnostics-snapshots.model';

@Injectable()
export class RemoteDiagnosticsSnapshotsEffects {
  private retryCounter = 0;
  private snapshotIdForDemo = 1;

  fetchInstallationVddList$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDList),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      mergeMap(([action, gatewaySerial]) =>
        combineLatest([
          of(action),
          of(gatewaySerial),
          this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, gatewaySerial, mainLogicalLinkFromTopology]) =>
        this.equipmentApi
          .getInstallationVddList(action.installationId.toString(), gatewaySerial)
          .pipe(
            map((response: SnapshotsConfigurationsBackupResponse) =>
              response.data.map((vddSeed) => {
                const vdd: Vdd = {
                  key: vddSeed.key,
                  logicalLink: mainLogicalLinkFromTopology, // !! undefined if there is no session
                };

                return vdd;
              })
            ),
            map((vdds) =>
              RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDListSuccess({
                vddList: vdds,
              })
            ),
            catchError(() => of(RemoteDiagnosticsSnapshotsActions.fetchInstallationVDDListFailed()))
          )
      )
    );
  });

  fetchSnapshots$: Observable<Action | string> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.fetchSnapshots),
      concatLatestFrom(() => [
        this.installationDetailsStore.gatewaySerial$,
        this.remoteDiagnosticsSnapshotsStore.lastNumberOfSnapshots$,
        this.authStore.isDemo$,
      ]),
      switchMap(([action, gatewaySerial, lastNumberOfSnapshots, isDemo]) =>
        this.equipmentApi
          .getSnapshots(action.installationId.toString(), gatewaySerial, action.snapshotType)
          .pipe(
            mergeMap((response) => {
              let sortedSnapshots = this.sortSnapshotsByDate(response.data);
              const wasSnapshotAdded =
                !!lastNumberOfSnapshots && lastNumberOfSnapshots < response.data.length;

              // Override actual data in demo mode
              if (isDemo) {
                sortedSnapshots = this.fetchSnapshotsInDemo(action, response, wasSnapshotAdded);
              }

              // Verify if user has added a snapshot and if they did display its data and make sure current month is selected
              if (wasSnapshotAdded && action.wasBackupGenerated) {
                return concat(
                  of(RemoteDiagnosticsSnapshotsActions.setFilterDate({ date: moment() })),
                  of(
                    RemoteDiagnosticsSnapshotsActions.fetchSnapshotsSuccess({
                      snapshots: sortedSnapshots,
                    })
                  ),
                  of(
                    RemoteDiagnosticsSnapshotsActions.snapshotSelected({
                      snapshot: sortedSnapshots[0],
                    })
                  ),
                  of(
                    RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetails({
                      installationId: action.installationId,
                      snapshotId: sortedSnapshots[0].id,
                    })
                  )
                );
              } else {
                return of(
                  RemoteDiagnosticsSnapshotsActions.fetchSnapshotsSuccess({
                    snapshots: sortedSnapshots,
                  })
                );
              }
            }),
            catchError((error) => of(error))
          )
      )
    );
  });

  fetchSnapshotDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetails),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      switchMap(([action, gatewaySerial]) =>
        this.equipmentApi
          .getSnapshotVdds(action.installationId.toString(), gatewaySerial, action.snapshotId)
          .pipe(
            // Repeat request if results's data is empty using 2s interval, at 5th attempt throw an error
            repeatWhen((result) => result.pipe(delay(2000))),
            concatMap((response, requestIndex) => {
              if (requestIndex === 15) {
                throw new Error('VDDs response timeout');
              } else {
                return of(response);
              }
            }),
            filter((result) => result.data.length > 0),
            take(1),
            map((response) => {
              response.data.forEach((vdd) => {
                vdd.key = BackupSeparator.removeBackupSeparator(vdd.key);
              });
              this.remoteDiagnosticsSnapshotsStore.showValues();

              return RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetailsSuccess({
                snapshotDetails: response.data,
              });
            }),
            catchError((error: ViError) => {
              this.showSnackbarError('+REMOTE_DIAGNOSTICS_BACKUPS.FETCH_BACKUP_DETAILS.FAILURE');

              return of(
                RemoteDiagnosticsSnapshotsActions.fetchSnapshotDetailsFailed({
                  error: error.message,
                })
              );
            }),
            takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
          )
      )
    );
  });

  createSnapshotOfTypeSnapshot$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.createSnapshotOfTypeSnapshot),
      concatLatestFrom(() => [
        this.remoteDiagnosticsSnapshotsStore.getSelectedParametersForNewSnapshot$,
        this.installationDetailsStore.gatewaySerial$,
        this.authStore.isDemo$,
      ]),
      switchMap(([action, vddListForSnapshot, gatewaySerial, isDemo]) => {
        // DIRTY HACK AS THE BACKEND IS NOT READY they validate key uniqueness...
        vddListForSnapshot.forEach(
          (vdd) => (vdd.key = BackupSeparator.addBackupSeparator(vdd.key, vdd.logicalLink))
        );
        // end of dirty hack

        return this.equipmentApi
          .createSnapshot(
            action.installationId.toString(),
            gatewaySerial,
            vddListForSnapshot,
            action.snapshotType
          )
          .pipe(
            map((response) => {
              if (isDemo) {
                this.remoteDiagnosticsSnapshotsService.snapshotDetailsForDemo.push({
                  id: this.snapshotIdForDemo.toString(),
                  vddList: vddListForSnapshot,
                });
                this.snapshotIdForDemo++;
              }

              return RemoteDiagnosticsSnapshotsActions.createSnapshotSuccess({
                snapshot: response.data,
                installationId: action.installationId,
                snapshotType: action.snapshotType,
              });
            }),
            catchError((error: ViError) => {
              this.showSnackbarError('+REMOTE_DIAGNOSTICS_BACKUPS.CREATE_BACKUP.FAILURE');

              return of(
                RemoteDiagnosticsSnapshotsActions.createSnapshotFailed({ error: error.message })
              );
            })
          );
      })
    );
  });

  createSnapshotOfTypeBackup$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.createSnapshotOfTypeBackup),
      concatLatestFrom(() => [
        this.remoteDiagnosticsSnapshotsStore.installationVddList$,
        this.installationDetailsStore.gatewaySerial$,
        this.authStore.isDemo$,
      ]),
      switchMap(([action, vddListForBackup, gatewaySerial, isDemo]) => {
        // DIRTY HACK AS THE BACKEND IS NOT READY they validate key uniqueness...
        vddListForBackup.forEach(
          (vdd) => (vdd.key = BackupSeparator.addBackupSeparator(vdd.key, vdd.logicalLink))
        );
        // end of dirty hack

        return this.equipmentApi
          .createSnapshot(
            action.installationId.toString(),
            gatewaySerial,
            vddListForBackup,
            action.snapshotType
          )
          .pipe(
            map((response) => {
              if (isDemo) {
                this.remoteDiagnosticsSnapshotsService.snapshotDetailsForDemo.push({
                  id: this.snapshotIdForDemo.toString(),
                  vddList: vddListForBackup,
                });
                this.snapshotIdForDemo++;
              }

              return RemoteDiagnosticsSnapshotsActions.createSnapshotSuccess({
                snapshot: response.data,
                installationId: action.installationId,
                snapshotType: action.snapshotType,
              });
            }),
            catchError((error: ViError) => {
              this.showSnackbarError('+REMOTE_DIAGNOSTICS_BACKUPS.CREATE_BACKUP.FAILURE');

              return of(
                RemoteDiagnosticsSnapshotsActions.createSnapshotFailed({ error: error.message })
              );
            })
          );
      })
    );
  });

  updateSnapshotWithVddContent$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContent),
      mergeMap((action) =>
        combineLatest([
          of(action),
          action.snapshotType === SnapshotType.BACKUP
            ? this.remoteDiagnosticsSnapshotsStore.getVddsForBackupWithContent$.pipe(take(1))
            : this.remoteDiagnosticsSnapshotsStore.getVddsForNewSnapshotWithContent$.pipe(take(1)),
          this.installationDetailsStore.gatewaySerial$.pipe(take(1)),
        ])
      ),
      switchMap(([action, vddsWithContent, gatewaySerial]) =>
        this.callMultipleUpdateSnapshotWithVddContentRequests(
          action.installationId.toString(),
          gatewaySerial,
          action.snapshotId,
          vddsWithContent
        ).pipe(
          map((resultList) => {
            const failedReqs = resultList.filter((request) => request.failed);

            if (failedReqs.length) {
              this.showSnackbarError('+REMOTE_DIAGNOSTICS_BACKUPS.CREATE_BACKUP.FAILURE');

              return RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContentFailed({
                error: 'update of snapshot failed',
              });
            } else {
              return RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContentSuccess({
                snapshotId: action.snapshotId,
                installationId: action.installationId,
              });
            }
          })
        )
      )
    );
  });

  cancelSnapshotVdds$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.cancelSnapshotVdd),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsSnapshotsStore.getVddsToCancel$.pipe(take(1)),
          this.installationDetailsStore.gatewaySerial$.pipe(take(1)),
        ])
      ),
      switchMap(([action, vddsToCancel, gatewaySerial]) => {
        const installationId = action.installationId;
        const snapshotId = action.snapshotId;

        return this.callMultipleCancelVddRequests(
          installationId.toString(),
          gatewaySerial,
          snapshotId,
          vddsToCancel
        ).pipe(
          map((resultList) => {
            const failedReqs = resultList.filter((request) => request.failed);

            if (failedReqs.length) {
              this.showSnackbarError('+REMOTE_DIAGNOSTICS_BACKUPS.CREATE_BACKUP.FAILURE');

              const vddKeys = failedReqs.map((request) => request.key);

              return RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddFailed({ error: vddKeys });
            } else {
              return RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddSuccess({
                installationId,
                snapshotId,
              });
            }
          })
        );
      })
    );
  });

  invokeUpdateSnapshotWithVddContent$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.createSnapshotSuccess),
      switchMap((action) =>
        of(
          RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContent({
            installationId: action.installationId,
            snapshotId: action.snapshot.id,
            snapshotType: action.snapshotType,
          })
        )
      )
    );
  });

  cancelSnapshotVddSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddSuccess),
      concatLatestFrom(() => [
        this.installationDetailsStore.gatewaySerial$,
        this.remoteDiagnosticsSnapshotsStore.currentSnapshotType$,
      ]),
      switchMap(([action, gatewaySerial, snapshotType]) =>
        this.equipmentApi
          .getSnapshot(action.installationId.toString(), gatewaySerial, action.snapshotId)
          .pipe(
            mergeMap((response) => {
              // try 5 times every 2 seconds to check for 'uploaded' snpashot status
              if (response.data.status !== SnapshotUploadStatus.UPLOADED) {
                throw new Error('snapshot not yet uploaded');
              } else {
                this.retryCounter = 0;

                if (snapshotType === SnapshotType.BACKUP) {
                  this.showSnackbarSuccess('+REMOTE_DIAGNOSTICS_BACKUPS.CREATE_BACKUP.SUCCESS');
                } else {
                  this.showSnackbarSuccess('+REMOTE_DIAGNOSTICS_SNAPSHOTS.CREATE_SNAPSHOT.SUCCESS');
                }

                return of(
                  RemoteDiagnosticsSnapshotsActions.fetchSnapshots({
                    installationId: action.installationId,
                    snapshotType,
                    wasBackupGenerated: true,
                  })
                );
              }
            }),
            retryWhen((errors) =>
              errors.pipe(
                delay(2000),
                tap(() => {
                  if (this.retryCounter === 5) {
                    throw new Error('upload of snapshot failed');
                  }

                  this.retryCounter++;
                })
              )
            ),
            catchError((error: globalThis.Error) => {
              this.retryCounter = 0;

              if (snapshotType === SnapshotType.BACKUP) {
                this.showSnackbarError('+REMOTE_DIAGNOSTICS_BACKUPS.CREATE_BACKUP.FAILURE');
              } else {
                this.showSnackbarError('+REMOTE_DIAGNOSTICS_SNAPSHOTS.CREATE_SNAPSHOT.FAILURE');
              }

              return of(
                RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddFailed({ error: error.message })
              );
            })
          )
      )
    );
  });

  invokeCancelSnapshotVdd$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsSnapshotsActions.updateSnapshotWithVddContentSuccess),
      concatLatestFrom(() => this.remoteDiagnosticsSnapshotsStore.getVddsToCancel$),
      switchMap(([action, vddToCancel]) => {
        const installationId = action.installationId;
        const vddsToCancel = vddToCancel;
        const snapshotId = action.snapshotId;

        if (vddsToCancel.length) {
          return of(
            RemoteDiagnosticsSnapshotsActions.cancelSnapshotVdd({
              installationId,
              snapshotId,
            })
          );
        } else {
          return of(
            RemoteDiagnosticsSnapshotsActions.cancelSnapshotVddSuccess({
              installationId,
              snapshotId,
            })
          );
        }
      })
    );
  });

  readVddValuesForSnapshot$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(RemoteDiagnosticsSnapshotsActions.triggerReadVddListValuesForSnapshot),
        concatLatestFrom(() => this.remoteDiagnosticsSnapshotsStore.installationVddList$),
        map(([action, vddList]) => {
          if (action.vdd) {
            this.vddDetailsListStore.getStructureAndValueForVddListAttempt(
              [BackupSeparator.addBackupSeparator(action.vdd.key, action.vdd.logicalLink)],
              action.gatewaySerial,
              action.vdd.logicalLink
            );
          } else {
            const vddListsByLogicalLinks = this.remoteDiagnosticsSnapshotsService.convertRawVddListToVddByLL(
              vddList
            );

            vddListsByLogicalLinks.forEach((vddListByLogicalLink) =>
              this.remoteDiagnosticsStore.openLogicalLinkBeforeVddReadingAttempt(
                action.gatewaySerial,
                vddListByLogicalLink.logicalLinkName,
                vddListByLogicalLink.vddNames
              )
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private equipmentApi: EquipmentApi,
    private actions$: Actions,
    private translate: TranslateService,
    private toastService: ViToastService,
    private remoteDiagnosticsSnapshotsStore: RemoteDiagnosticsSnapshotsStore,
    private installationDetailsStore: InstallationDetailsStore,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private vddDetailsListStore: VddDetailsListStore,
    private remoteDiagnosticsSnapshotsService: RemoteDiagnosticsSnapshotsService,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService,
    private authStore: AuthStore
  ) {}

  private fetchSnapshotsInDemo(
    action: {
      installationId: number;
      snapshotType: SnapshotType;
      wasBackupGenerated: boolean;
    },
    response: SnapshotsResponse,
    wasSnapshotAdded: boolean
  ): Snapshot[] {
    // Demo mode - keep snapshots in service and display them again after tab change
    if (!action.wasBackupGenerated && !wasSnapshotAdded) {
      // Fill array if empty - initial state
      if (action.snapshotType === SnapshotType.BACKUP) {
        if (!this.remoteDiagnosticsSnapshotsService.backupsForDemo.length) {
          this.remoteDiagnosticsSnapshotsService.backupsForDemo = response.data;
        }
        const storedSnapshots = this.remoteDiagnosticsSnapshotsService.backupsForDemo;

        return this.sortSnapshotsByDate(storedSnapshots);
      }

      if (action.snapshotType === SnapshotType.SNAPSHOT) {
        if (!this.remoteDiagnosticsSnapshotsService.snapshotsForDemo.length) {
          this.remoteDiagnosticsSnapshotsService.snapshotsForDemo = response.data;
        }
        const storedSnapshots = this.remoteDiagnosticsSnapshotsService.snapshotsForDemo;

        return this.sortSnapshotsByDate(storedSnapshots);
      }
    }

    // Demo mode - set current date to the newest backup/snapshot
    if (action.wasBackupGenerated || wasSnapshotAdded) {
      const data = response.data;
      data[0].createdAt = new Date().toISOString();
      if (action.snapshotType === SnapshotType.BACKUP) {
        this.remoteDiagnosticsSnapshotsService.backupsForDemo.push(...data);

        return this.sortSnapshotsByDate(this.remoteDiagnosticsSnapshotsService.backupsForDemo);
      } else {
        const snapshotsDetails = this.remoteDiagnosticsSnapshotsService.snapshotDetailsForDemo;
        data[0].id = snapshotsDetails[snapshotsDetails.length - 1].id;
        this.remoteDiagnosticsSnapshotsService.snapshotsForDemo.push(...data);

        return this.sortSnapshotsByDate(this.remoteDiagnosticsSnapshotsService.snapshotsForDemo);
      }
    }
  }

  private callMultipleCancelVddRequests(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string,
    vddsToCancel: string[]
  ): Observable<any> {
    const cancelVddRequests = vddsToCancel.map((vddName) =>
      this.mapToCancelVddRequest(installationId, gatewaySerial, snapshotId, vddName)
    );

    return forkJoin(cancelVddRequests);
  }

  private mapToCancelVddRequest(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string,
    key: string
  ): Observable<any> {
    return this.equipmentApi.cancelVdd(installationId, gatewaySerial, snapshotId, key).pipe(
      map(() => of({ key, failed: false })),
      catchError(() => of({ key, failed: true }))
    );
  }

  private callMultipleUpdateSnapshotWithVddContentRequests(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string,
    vddsToUpdate: VddWithContent[]
  ): Observable<SnapshotUpdateResponseTransformed[]> {
    // more or less we have 150 vdds
    // we make 5 requests with 30 vdds per request
    // we cannot make one request as we can possibly encounter 413 too large payload error
    // thus we split it into multiple calls
    const numberOfVddsToUpdatePerRequest = 30;
    const vddGroups = this.chunkArray<VddWithContent>(vddsToUpdate, numberOfVddsToUpdatePerRequest);
    const updateVddRequests = vddGroups.map((vds: VddWithContent[]) =>
      this.mapToUpdateSnapshotWithVddContentRequest(installationId, gatewaySerial, snapshotId, vds)
    );

    return forkJoin(updateVddRequests);
  }

  private mapToUpdateSnapshotWithVddContentRequest(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string,
    vddsWithContent: VddWithContent[]
  ): Observable<SnapshotUpdateResponseTransformed> {
    return this.equipmentApi
      .updateSnapshot(installationId, gatewaySerial, snapshotId, vddsWithContent)
      .pipe(
        map(() => ({ failed: false })),
        catchError(() => of({ failed: true }))
      );
  }

  private showSnackbarError(key: string): void {
    const message = this.translate.instant(key);
    this.toastService.error({
      message,
    });
  }

  private showSnackbarSuccess(key: string): void {
    const message = this.translate.instant(key);
    this.toastService.success({
      message,
    });
  }

  private chunkArray<T>(myArray: T[], chunkSize: number): T[][] {
    let index = 0;
    const arrayLength = myArray.length;
    const tempArray = [];
    let myChunk: any;

    for (index = 0; index < arrayLength; index += chunkSize) {
      myChunk = myArray.slice(index, index + chunkSize);
      tempArray.push(myChunk);
    }

    return tempArray;
  }

  private sortSnapshotsByDate(snapshots: Snapshot[]): Snapshot[] {
    return snapshots.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      if (dateA < dateB) {
        return 1;
      }
      if (dateA > dateB) {
        return -1;
      }

      return 0;
    });
  }
}
